/**
 * MobilePageHeader — compact sticky header for phone/tablet.
 *
 * Only rendered when the viewport is < md (see AppShell.jsx). Contains:
 *   • Hamburger to open MobileNavigationDrawer
 *   • Compact AYOBELL wordmark
 *   • Page title (derived from route)
 *   • Notifications bell (retains existing behaviour)
 *   • Overflow menu — hosts the lower-priority controls that live in the
 *     desktop TopBar (site switcher, theme, help centre, sign out) so we
 *     don't cram seven controls into a 44 px header.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu, MoreVertical, Sun, Moon, HelpCircle, LogOut, Building2, Smartphone,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useOnboarding } from "@/context/OnboardingContext";
import api from "@/lib/api";
import MobileNavigationDrawer from "@/components/mobile/MobileNavigationDrawer";

const ROUTE_LABELS = {
  "": "Dashboard",
  "rota": "Rota",
  "staff-schedule": "Schedule",
  "client-care-cost": "Client Care Cost",
  "payroll": "Payroll",
  "mar-chart": "MAR Chart",
  "cd-ledger": "CD Ledger",
  "delivery-notes": "Delivery Notes",
  "my-shifts": "My Shifts",
  "calendar": "Appointments",
  "audit": "Audit Centre",
  "care-plans": "Care Plans",
  "risk-register": "Risk Register",
  "time-off-requests": "Leave & Holidays",
  "family-portal": "Family Portal",
  "family-inbox": "Family Inbox",
  "sites": "Sites",
  "users": "Staff & Users",
  "clients": "Service Users",
  "mobile": "Mobile",
  "training": "Training",
  "supervisions": "Supervisions",
  "medication-competency": "Med Competency",
  "audits": "Audits",
  "incidents": "Incidents",
  "complaints": "Complaints",
  "outcomes": "Outcomes",
  "maintenance": "Maintenance",
};

function humanize(seg) {
  return ROUTE_LABELS[seg] || seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function pageTitleFromPath(pathname) {
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length === 0) return "Dashboard";
  const last = segs[segs.length - 1];
  if (/^[0-9a-f]{8}-/i.test(last)) {
    return humanize(segs[segs.length - 2] || "");
  }
  return humanize(last);
}

function OverflowMenu({ onSignOut }) {
  const [open, setOpen] = useState(false);
  const { theme, toggle: toggleTheme } = useTheme();
  const { setOpen: setHelpOpen, status } = useOnboarding();
  const { user, setUser } = useAuth();
  const [sites, setSites] = useState([]);
  const [siteSwitching, setSiteSwitching] = useState(false);

  const isManager = user?.role === "admin" || user?.role === "manager";
  const currentSite = sites.find((s) => s.id === user?.site_id);
  const helpPending = status ? (status.total - status.completed_count) : 0;

  useEffect(() => {
    if (!open || !isManager) return;
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
  }, [open, isManager]);

  const pickSite = async (siteId) => {
    setSiteSwitching(true);
    try {
      const { data } = await api.patch("/auth/me", { site_id: siteId });
      setUser(data);
      window.location.reload();
    } catch { /* toast handled by api */ }
    finally { setSiteSwitching(false); setOpen(false); }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="mobile-header-menu"
        aria-label="More options"
        className="relative inline-flex items-center justify-center w-11 h-11 rounded-md text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-slate-200 dark:active:bg-slate-700"
      >
        <MoreVertical className="w-5 h-5" />
        {helpPending > 0 && (
          <span
            className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-sky-600 text-white text-[9px] font-bold flex items-center justify-center tabular-nums"
            data-testid="mobile-header-help-badge"
          >
            {helpPending}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-1 mt-1 z-50 min-w-[240px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg rounded-md overflow-hidden"
            data-testid="mobile-header-menu-panel"
          >
            <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-800">
              <div className="text-xs font-medium text-slate-900 dark:text-slate-100 truncate">
                {user?.name || user?.email}
              </div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-widest">
                {(user?.role || "").replace(/_/g, " ")}
              </div>
            </div>

            {isManager && (
              <>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                  Site
                </div>
                <button
                  onClick={() => pickSite(null)}
                  disabled={siteSwitching}
                  data-testid="mobile-header-site-all"
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${
                    !user?.site_id ? "text-emerald-700 dark:text-emerald-400 font-semibold" : "text-slate-700 dark:text-slate-200"
                  }`}
                >
                  <Building2 className="w-4 h-4" /> All sites
                </button>
                {sites.slice(0, 6).map((s) => (
                  <button
                    key={s.id}
                    onClick={() => pickSite(s.id)}
                    disabled={siteSwitching}
                    data-testid={`mobile-header-site-${s.id}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${
                      s.id === user?.site_id ? "text-emerald-700 dark:text-emerald-400 font-semibold" : "text-slate-700 dark:text-slate-200"
                    }`}
                  >
                    <Building2 className="w-4 h-4 opacity-70" /> <span className="truncate">{s.name}</span>
                  </button>
                ))}
                <div className="border-t border-slate-100 dark:border-slate-800" />
              </>
            )}

            <button
              onClick={() => { setOpen(false); toggleTheme(); }}
              data-testid="mobile-header-theme"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === "dark" ? "Light mode" : "Dark mode"}
            </button>

            <button
              onClick={() => { setOpen(false); setHelpOpen(true); }}
              data-testid="mobile-header-help"
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <span className="inline-flex items-center gap-2"><HelpCircle className="w-4 h-4" /> Help Centre</span>
              {helpPending > 0 && (
                <span className="text-[10px] bg-sky-600 text-white px-1.5 py-0.5 rounded-sm tabular-nums">{helpPending}</span>
              )}
            </button>

            <Link
              to="/mobile"
              onClick={() => setOpen(false)}
              data-testid="mobile-header-quicklog"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              <Smartphone className="w-4 h-4" /> Quick log (offline)
            </Link>

            <button
              onClick={() => { setOpen(false); onSignOut(); }}
              data-testid="mobile-header-signout"
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border-t border-slate-100 dark:border-slate-800"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
            {isManager && currentSite && (
              <div className="px-3 py-1.5 text-[10px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-800">
                Active site: <b className="text-slate-700 dark:text-slate-200">{currentSite.name}</b>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function MobilePageHeader() {
  const location = useLocation();
  const { logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const title = pageTitleFromPath(location.pathname);

  return (
    <header
      className="lg:hidden sticky top-0 z-30 h-14 shrink-0 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-2 flex items-center gap-2"
      data-testid="mobile-header"
    >
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        aria-label="Open navigation"
        data-testid="mobile-header-menu-btn"
        className="inline-flex items-center justify-center w-11 h-11 rounded-md text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-slate-200 dark:active:bg-slate-700"
      >
        <Menu className="w-5 h-5" />
      </button>

      <Link
        to="/"
        className="flex items-center gap-2 min-w-0 flex-1"
        data-testid="mobile-header-title"
      >
        <img
          src="/brand/icon-192.png"
          alt="Ayobell"
          className="w-7 h-7 object-contain shrink-0"
        />
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-slate-400 leading-none">AYOBELL</div>
          <div className="text-sm font-heading font-semibold text-slate-900 dark:text-slate-100 truncate leading-tight">
            {title}
          </div>
        </div>
      </Link>

      <NotificationBell />
      <OverflowMenu onSignOut={logout} />

      <MobileNavigationDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </header>
  );
}
